import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconDna } from './Icons';
import { KEY_LAB_META } from '../lib/dashboardHelpers';
import LabGauge from './LabGauge';
import { useLabRange, type LabRangeType } from '../lib/labRangeContext';
import LabRangeToggle from './LabRangeToggle';

interface LabEntry {
  marker: string;
  value: number;
  unit: string;
  ref_low: number | null;
  ref_high: number | null;
  panel: string;
  date: string;
}

interface Props {
  keyLabs: LabEntry[];
}

type LabStatus = 'optimal' | 'borderline' | 'out' | 'unknown';

function getStatus(lab: LabEntry, rangeType: LabRangeType): LabStatus {
  const meta = KEY_LAB_META[lab.marker];
  const inOptimal = meta?.optLow != null && meta?.optHigh != null
    ? lab.value >= meta.optLow && lab.value <= meta.optHigh
    : null;
  const inStandard = lab.ref_low != null && lab.ref_high != null
    ? lab.value >= lab.ref_low && lab.value <= lab.ref_high
    : null;

  if (rangeType === 'standard') {
    if (inStandard === true)  return 'optimal';   // "in range" when standard
    if (inStandard === false) return 'out';
    return 'unknown';
  }
  // optimal mode
  if (inOptimal === true)                                  return 'optimal';
  if (inOptimal === false && inStandard === true)           return 'borderline';
  if (inStandard === false || inOptimal === false)          return 'out';
  return 'unknown';
}

const STATUS_CONFIG = {
  borderline: { valueColor: '#F59E0B', bg: 'bg-amber-50 border-amber-100', badge: '◐ Borderline', badgeClass: 'text-amber-500' },
  out:        { valueColor: '#EF4444', bg: 'bg-red-50 border-red-100',     badge: '⚠ Out of range', badgeClass: 'text-red-500' },
  unknown:    { valueColor: '#64748B', bg: 'bg-gray-50 border-gray-100',   badge: '? Unknown',      badgeClass: 'text-gray-500' },
  optimal:    { valueColor: '#00A896', bg: 'bg-emerald-50/60 border-emerald-100', badge: '✓ Optimal', badgeClass: 'text-emerald-600' },
};

function LabCard({ lab, status, navigate, rangeType }: { lab: LabEntry; status: LabStatus; navigate: (path: string) => void; rangeType: LabRangeType }) {
  const meta = KEY_LAB_META[lab.marker];
  const C = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const rangeLabel = rangeType === 'optimal' ? 'Opt' : 'Std';

  const rangeHint = (() => {
    if (rangeType === 'standard') {
      if (lab.ref_low != null && lab.ref_high != null) return `Ref: ${lab.ref_low}\u2013${lab.ref_high} ${lab.unit}`;
      return null;
    }
    if (meta?.optLow != null && meta?.optHigh != null && meta.optHigh < 9000)
      return `Opt: ${meta.optLow}\u2013${meta.optHigh} ${lab.unit}`;
    if (meta?.optLow != null && meta?.optHigh != null && meta.optHigh >= 9000)
      return `Opt: >${meta.optLow} ${lab.unit}`;
    return null;
  })();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate('/labs')}
      onKeyDown={e => { if (e.key === 'Enter') navigate('/labs'); }}
      className={`card p-4 border ${C.bg} hover:shadow-card-hover transition-shadow cursor-pointer flex flex-col`}
    >
      {meta && (
        <span
          className="inline-block font-semibold rounded-full mb-1 self-start"
          style={{ backgroundColor: `${meta.phenotypeColor}18`, color: meta.phenotypeColor, fontSize: '10px', padding: '2px 7px' }}
        >
          {meta.phenotype}
        </span>
      )}

      <p className="text-xs text-arete-muted truncate">{lab.marker}</p>

      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-lg font-bold" style={{ color: C.valueColor }}>{lab.value}</span>
        <span className="text-xs text-arete-muted">{lab.unit}</span>
      </div>

      {C.badge && (
        <div className="flex items-center gap-1.5 mt-0.5">
          <p className={`text-xs font-medium ${C.badgeClass}`}>{C.badge}</p>
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(100,116,139,0.10)', color: '#64748B' }}>{rangeLabel}</span>
        </div>
      )}
      {(status === 'borderline' || status === 'out') && rangeHint && (
        <p className="text-xs text-arete-muted leading-tight">{rangeHint}</p>
      )}

      {/* Range gauge */}
      <LabGauge
        value={lab.value}
        refLow={lab.ref_low}
        refHigh={lab.ref_high}
        optLow={meta?.optLow}
        optHigh={meta?.optHigh}
        markerColor={C.valueColor}
      />

      <div className="flex items-center justify-between mt-auto pt-1">
        <span className="text-arete-muted" style={{ fontSize: '10px' }}>
          {new Date(lab.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
        {meta && (
          <a
            href={meta.citationUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="text-arete-blue hover:underline py-1 -my-1 px-1 -mx-1"
            style={{ fontSize: '10px' }}
          >
            Research ↗
          </a>
        )}
      </div>
    </div>
  );
}

export default React.memo(function DashboardKeyLabs({ keyLabs }: Props) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const { rangeType } = useLabRange();

  if (keyLabs.length === 0) return null;

  const statuses = keyLabs.map(lab => ({ lab, status: getStatus(lab, rangeType) }));
  const passing = statuses.filter(s => s.status === 'optimal');
  const nonOptimal = statuses.filter(s => s.status !== 'optimal');

  const latestDate = keyLabs.reduce((latest, lab) => lab.date > latest ? lab.date : latest, keyLabs[0].date);
  const dateLabel = new Date(latestDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="section-label flex items-center gap-1.5">
          <IconDna size={14} color="#64748B" /> Mental Health Labs
        </h3>
        <div className="flex items-center gap-3">
          <LabRangeToggle compact />
          <a href="/labs" className="text-xs text-arete-blue hover:underline">View all labs →</a>
        </div>
      </div>

      {/* Summary line */}
      <p className="text-xs text-arete-muted mb-3">
        {nonOptimal.length} of {keyLabs.length} labs flagged · {dateLabel}
      </p>

      {/* Non-optimal labs — always visible */}
      {nonOptimal.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3.5 mb-3.5">
          {nonOptimal.map(({ lab, status }) => (
            <LabCard key={lab.marker} lab={lab} status={status} navigate={navigate} rangeType={rangeType} />
          ))}
        </div>
      )}

      {/* Passing labs — expandable */}
      {passing.length > 0 && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="w-full card border border-emerald-100 bg-emerald-50/40 px-4 py-3 flex items-center gap-3 hover:shadow-card-hover transition-shadow text-left"
        >
          <span className="text-emerald-500 text-sm">✓</span>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-arete-navy dark:text-gray-100">
              {passing.length} of {keyLabs.length} labs {rangeType === 'optimal' ? 'optimal' : 'in range'}
            </span>
            <span className="text-xs text-arete-muted ml-2 hidden sm:inline">
              {passing.map(o => o.lab.marker).join(' · ')}
            </span>
          </div>
          <span className="text-xs text-arete-muted flex-shrink-0">Show all ▾</span>
        </button>
      )}

      {/* Expanded passing labs */}
      {showAll && passing.length > 0 && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3.5 mb-3.5">
            {passing.map(({ lab, status }) => (
              <LabCard key={lab.marker} lab={lab} status={status} navigate={navigate} rangeType={rangeType} />
            ))}
          </div>
          <button
            onClick={() => setShowAll(false)}
            className="text-xs text-arete-muted hover:text-arete-forest transition-colors"
          >
            Show flagged only ▴
          </button>
        </>
      )}
    </div>
  );
});
